import type Withholding from "@/types/withholding";
import type { DiscountType, InvoiceLine } from "@/types/invoice";

import { getLineNet } from "@/utils/invoiceLineNet";
import { resolveWithholdingAmount } from "@/utils/withholdings";
import { isIvaTax, isWithholdingTax, projectTaxes, withholdingFamily } from "@/utils/taxes";

export type InvoiceTotalsSummary = {
  netTotal: number;
  ivaTotal: number;
  otherTaxesTotal: number;
  withholdingTotal: number;
  discountTotal: number;
  linesTotal: number;
  total: number;
};

export function getLineTaxTotal(line: InvoiceLine, country?: string | null): number {
  if (!line.tax_details || line.tax_details.length === 0) return 0;
  return line.tax_details.filter(tax => !isWithholdingTax(tax, country)).reduce(
    (sum: number, tax: any) => sum + (tax.tax_amount || 0),
    0
  );
}

export function getLineIvaTotal(line: InvoiceLine, country?: string | null): number {
  if (!line.tax_details || line.tax_details.length === 0) return 0;
  const ivaTaxes = line.tax_details.filter((tax: any) => isIvaTax(tax, country));
  return ivaTaxes.reduce((sum: number, tax: any) => sum + (tax.tax_amount || 0), 0);
}

export function getLineOtherTaxesTotal(line: InvoiceLine, country?: string | null): number {
  if (!line.tax_details || line.tax_details.length === 0) return 0;
  const otherTaxes = line.tax_details.filter((tax: any) => !isWithholdingTax(tax, country) && !isIvaTax(tax, country));
  return otherTaxes.reduce((sum: number, tax: any) => sum + (tax.tax_amount || 0), 0);
}

// why: single source of truth for the invoice total. Lived in composables/useInvoiceTotals.ts, which
// the application layer (renditions) cannot import; moved here (a pure leaf util, next to its own deps)
// so both payments (via the composable re-export) and renditions read the exact same formula.
export function calculateInvoiceTotals(params: {
  country?: string | null;
  lines: InvoiceLine[];
  headerWithholdings?: Withholding[];
  tipAmount?: number;
  discountTotal?: number;
  currencyDecimalPlaces?: number;
  discountType?: DiscountType;
}): InvoiceTotalsSummary {
  const { headerWithholdings = [], tipAmount, discountTotal, discountType, country } = params;
  const lines = params.lines.map(line => {
    const projected = projectTaxes(line.tax_details || [], line.withholdings || [], country);
    return { ...line, tax_details: projected.taxes, withholdings: projected.withholdings };
  });
  const tip = typeof tipAmount === "number" ? tipAmount : 0;
  let discount = typeof discountTotal === "number" ? discountTotal : 0;

  let netTotal = lines.reduce((sum: number, line: InvoiceLine) => sum + getLineNet(line, discountType), 0);
  let ivaTotal = lines.reduce((sum: number, line: InvoiceLine) => sum + getLineIvaTotal(line, country), 0);
  let otherTaxesTotal = lines.reduce(
    (sum: number, line: InvoiceLine) => sum + getLineOtherTaxesTotal(line, country),
    0
  );

  let linesTotal = lines.reduce((sum: number, line: InvoiceLine) => {
    return sum + getLineNet(line, discountType) + getLineTaxTotal(line, country);
  }, 0);

  const headerFamilies = new Set(headerWithholdings
    .filter(w => w.percentage != null || w.tax_amount != null)
    .map(w => withholdingFamily(w, country)));
  const withholdingTotalFromLines = lines.reduce((sum: number, line: InvoiceLine) => {
    const lineNetTotal = getLineNet(line, discountType);
    const lineIvaTotal = getLineIvaTotal(line, country);
    const withholdings = (line.withholdings || []) as Withholding[];
    return (
      sum +
      withholdings.filter(w => !headerFamilies.has(withholdingFamily(w, country))).reduce((acc: number, w: Withholding) => {
        const withholdingAmount = resolveWithholdingAmount(w, lineNetTotal, lineIvaTotal, country);
        return acc + withholdingAmount;
      }, 0)
    );
  }, 0);

  const withholdingTotalFromHeader = headerWithholdings.reduce(
    (sum: number, w: Withholding) => {
      const withholdingAmount = resolveWithholdingAmount(w, netTotal, ivaTotal, country);
      return sum + withholdingAmount;
    },
    0
  );

  let withholdingTotal = withholdingTotalFromLines + withholdingTotalFromHeader;
  let total = linesTotal - withholdingTotal + tip - discount;

  // Round totals for zero-decimal currencies (e.g. CLP)
  const dp = params.currencyDecimalPlaces ?? 2;
  if (dp === 0) {
    netTotal = Math.round(netTotal);
    ivaTotal = Math.round(ivaTotal);
    otherTaxesTotal = Math.round(otherTaxesTotal);
    withholdingTotal = Math.round(withholdingTotal);
    discount = Math.round(discount);
    linesTotal = Math.round(linesTotal);
    total = Math.round(total);
  }

  return {
    netTotal,
    ivaTotal,
    otherTaxesTotal,
    withholdingTotal,
    discountTotal: discount,
    linesTotal,
    total,
  };
}
